import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import get from 'lodash/get';

import { useSnackBar } from '@sbv-clients/ui-shared';
import { getApiErrorMessage } from '@sbv-clients/util-misc';

import {
  Api,
  AuthorizationApiAuthRolesRoleIdPutRequest,
  CreateBulkUserRoleMapping,
  CreateRoleDTO,
  RolesCacheKeys,
  useApi,
} from '../index';

export const useAdminRolesGet = () => {
  const api = useApi();
  return useQuery({
    queryKey: [RolesCacheKeys.Roles],
    queryFn: () =>
      api.authorizationApi
        .authRolesGet()
        .then((response) => response?.data ?? []),
    retry: false,
    refetchOnWindowFocus: false,
  });
};

export const useAdminRolesIdGet = () => {
  const api = useApi();
  return useQuery({
    queryKey: [RolesCacheKeys.Roles],
    queryFn: () =>
      api.authorizationApi.authRolesGet().then(
        (response) =>
          response?.data?.map((role) => ({
            roleId: role.roleId,
            roleName: role.name,
            rolePermissions: role.rolePermissions,
          })) ?? []
      ),
    retry: false,
    refetchOnWindowFocus: false,
  });
};

export const useAdminPermissionsGet = () => {
  const api = useApi();
  return useQuery({
    queryKey: [RolesCacheKeys.Permissions],
    queryFn: () => api.authorizationApi.authPermissionsGet(),
    retry: false,
    refetchOnWindowFocus: false,
  });
};

const createRole = (api: Api) => (request: CreateRoleDTO) => {
  return api.authorizationApi.authRolePost({ createRoleDTO: request });
};

export const useCreateRoleApi = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  const { notifyError } = useSnackBar();

  return useMutation(createRole(api), {
    onSuccess: () => {
      queryClient.invalidateQueries([RolesCacheKeys.Roles]);
    },
    onError: (error) => {
      notifyError(getApiErrorMessage(error));
    },
  });
};

const editRole =
  (api: Api) => (request: AuthorizationApiAuthRolesRoleIdPutRequest) => {
    return api.authorizationApi.authRolesRoleIdPut(request);
  };

export const useEditRoleApi = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  const { notifyError } = useSnackBar();

  return useMutation(editRole(api), {
    onSuccess: () => {
      queryClient.invalidateQueries([RolesCacheKeys.Roles]);
    },
    onError: (error) => {
      notifyError(getApiErrorMessage(error));
    },
  });
};

const deleteRole = (api: Api) => (roleId: number) =>
  api.authorizationApi.authRolesRoleIdDelete({ roleId });

export const useDeleteRole = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  const { notifyError, notifySuccess } = useSnackBar();

  return useMutation(deleteRole(api), {
    onSuccess: async () => {
      queryClient.invalidateQueries([RolesCacheKeys.Roles]);
      notifySuccess('Role deleted successfully');
    },
    onError: async (error) => {
      const message = get(error, 'response.data.message', '');
      notifyError(message);
    },
  });
};

export const useUsersGet = () => {
  const api = useApi();
  return useQuery({
    queryKey: [RolesCacheKeys.Users],
    queryFn: () =>
      api.authorizationApi
        .authUsersGet()
        .then((response) => response?.data ?? []),
    retry: false,
    refetchOnWindowFocus: false,
  });
};

const usersBulkMapRole =
  (api: Api) => (createBulkUserRoleMapping: CreateBulkUserRoleMapping) =>
    api.authorizationApi.authUsersBulkMapRolePut({
      createBulkUserRoleMapping,
    });

export const useUsersBulkMapRole = () => {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation(usersBulkMapRole(api), {
    onSuccess: () => {
      queryClient.invalidateQueries([RolesCacheKeys.Users]);
    },
  });
};
